import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import {
  X, Send, Upload, Bot, FileText,
  Loader2, CheckCircle, Circle, Sparkles, ChevronDown,
  ArrowLeft, AlertTriangle, Save, Edit3, Plus, Trash2, UserPlus,
  PauseCircle, DollarSign, Eye, EyeOff
} from 'lucide-react';
import VoiceControls from '../shared/VoiceControls';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import {
  LOAN_TYPE_OPTIONS, DEBT_SCHEDULE_TYPES, FIELD_SECTIONS, REQUIRED_FIELDS,
  crossPopulateFields, formatValue, EMPTY_DEBT, DEBT_FIELDS, REVERSE_LOOKUP
} from './loanFieldConstants';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SSN_KEYS = new Set(['social_security_number', 'owner_ssn', 'ssn_itin']);
const formatSSN = (value) => {
  const digits = (value || '').toString().replace(/\D/g, '').slice(0, 9);
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
};

const SSNValue = ({ value }) => {
  const [visible, setVisible] = React.useState(false);
  if (!value) return <span className="text-slate-300">---</span>;
  const str = String(value);
  const masked = str.length <= 4 ? '***-**-' + str : '***-**-' + str.slice(-4);
  return (
    <span className="inline-flex items-center gap-1">
      <span className="font-medium text-slate-800">{visible ? str : masked}</span>
      <button onClick={() => setVisible(!visible)} className="text-slate-400 hover:text-slate-600 p-0.5" data-testid="ssn-toggle">
        {visible ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
      </button>
    </span>
  );
};

const AIApplicationFlow = ({ onClose, onSuccess, resumeApp }) => {
  const { user } = useAuth();
  const [step, setStep] = useState(resumeApp ? 'review' : 'select');
  const [loanTypes, setLoanTypes] = useState(resumeApp ? [resumeApp.product_type] : []);

  // Lock body scroll when modal is open (critical for iOS)
  useEffect(() => {
    const scrollY = window.scrollY;
    const html = document.documentElement;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
    html.style.overflow = 'hidden';
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      html.style.overflow = '';
      window.scrollTo(0, scrollY);
    };
  }, []);

  // Filter loan types based on user's allowed loan types (if set)
  const availableLoanTypes = user?.allowed_loan_types?.length > 0
    ? LOAN_TYPE_OPTIONS.filter(opt => user.allowed_loan_types.includes(opt.id))
    : LOAN_TYPE_OPTIONS;
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(() => {
    if (resumeApp) {
      const data = {};
      const pt = resumeApp.product_type;
      const dataKey = `${pt}_data`;
      if (resumeApp[dataKey]) data[dataKey] = resumeApp[dataKey];
      return data;
    }
    return {};
  });
  const [progress, setProgress] = useState(0);
  const [lastAIResponse, setLastAIResponse] = useState('');
  const [voiceError, setVoiceError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [aiInitialized, setAiInitialized] = useState(false);
  // Debt schedule state
  const [hasDebts, setHasDebts] = useState(resumeApp?.has_business_debts ?? null);
  const [debtItems, setDebtItems] = useState(resumeApp?.debt_schedule?.length > 0 ? resumeApp.debt_schedule : [{ ...EMPTY_DEBT }]);
  const [resumeAppId] = useState(resumeApp?.id || null);

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // When resuming, no longer auto-start chat — user goes to review first

  const needsDebtSchedule = loanTypes.some(t => DEBT_SCHEDULE_TYPES.includes(t));

  const toggleLoanType = (id) => {
    setLoanTypes(prev => prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]);
  };

  const startApplication = () => {
    if (loanTypes.length === 0) return;
    setStep('review');
  };

  const goToAIChat = () => {
    setStep('chat');
    if (!aiInitialized) {
      setAiInitialized(true);
      if (resumeApp) {
        const typeName = resumeApp.product_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        sendToAI(`I'm resuming my ${typeName} application. I've already started filling out some fields. Please review what I have and continue asking about what's still missing.`);
      } else {
        const typeNames = loanTypes.map(t => LOAN_TYPE_OPTIONS.find(o => o.id === t)?.label || t).join(', ');
        sendToAI(`I want to apply for: ${typeNames}. Let's get started.`);
      }
    }
  };

  const sendToAI = useCallback(async (text) => {
    if (!text.trim() || loading) return;
    const userMsg = { role: 'user', content: text };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);
    try {
      const conversation = [...messages, userMsg].map(m => ({ role: m.role, content: m.content }));
      const response = await axios.post(`${BACKEND_URL}/api/ai-application/chat`, {
        message: text, loan_types: loanTypes, current_data: formData, conversation
      });
      const { message: aiMsg, field_updates, progress: aiProgress } = response.data;
      if (field_updates && Object.keys(field_updates).length > 0) {
        const crossPopulated = loanTypes.length > 1 ? crossPopulateFields(field_updates, loanTypes) : field_updates;
        setFormData(prev => {
          const updated = { ...prev };
          for (const [key, fields] of Object.entries(crossPopulated)) {
            if (fields && Object.keys(fields).length > 0) updated[key] = { ...(updated[key] || {}), ...fields };
          }
          return updated;
        });
      }
      if (aiProgress !== undefined) setProgress(aiProgress);
      setMessages(prev => [...prev, { role: 'assistant', content: aiMsg }]);
      setLastAIResponse(aiMsg);
    } catch (error) {
      console.error('AI chat error:', error);
      setMessages(prev => [...prev, { role: 'assistant', content: 'I had trouble processing that. Could you try rephrasing?' }]);
    } finally {
      setLoading(false);
    }
  }, [loading, messages, loanTypes, formData]);

  const handleSend = (e) => { e?.preventDefault(); if (input.trim()) sendToAI(input); };
  const handleKeyPress = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } };

  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingDoc(true);
    setMessages(prev => [...prev, { role: 'user', content: `[Uploaded document: ${file.name}]` }]);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('loan_types', loanTypes.join(','));
      const response = await axios.post(`${BACKEND_URL}/api/ai-application/extract-doc`, fd);
      const { message: aiMsg, field_updates, progress: docProgress } = response.data;
      if (field_updates && Object.keys(field_updates).length > 0) {
        const crossPopulated = loanTypes.length > 1 ? crossPopulateFields(field_updates, loanTypes) : field_updates;
        setFormData(prev => {
          const updated = { ...prev };
          for (const [key, fields] of Object.entries(crossPopulated)) {
            if (fields && Object.keys(fields).length > 0) updated[key] = { ...(updated[key] || {}), ...fields };
          }
          return updated;
        });
      }
      if (docProgress !== undefined && docProgress > progress) setProgress(docProgress);
      setMessages(prev => [...prev, { role: 'assistant', content: aiMsg || `I've processed "${file.name}" and extracted the available information.` }]);
      setUploadingDoc(false);
      setTimeout(() => {
        const extractedCount = Object.values(field_updates || {}).reduce((sum, fields) => sum + Object.keys(fields || {}).length, 0);
        sendToAI(`I just uploaded "${file.name}" and ${extractedCount} fields were extracted. Please continue asking me about the remaining fields.`);
      }, 500);
    } catch (error) {
      console.error('Doc upload error:', error);
      setMessages(prev => [...prev, { role: 'assistant', content: `I had trouble reading "${file.name}". You can still provide the information verbally.` }]);
      setUploadingDoc(false);
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Save as incomplete draft
  const handleSaveForLater = async () => {
    setSavingDraft(true);
    try {
      const groupId = loanTypes.length > 1 ? crypto.randomUUID() : null;
      for (const type of loanTypes) {
        const dataKey = `${type}_data`;
        const payload = {
          product_type: type,
          status: 'incomplete',
          last_completed_step: step,
          has_business_debts: hasDebts || false,
          debt_schedule: hasDebts ? debtItems.filter(d => d.lender_name).map(d => ({
            ...d,
            original_amount: d.original_amount ? Number(d.original_amount) : null,
            current_balance: d.current_balance ? Number(d.current_balance) : null,
            interest_rate: d.interest_rate ? Number(d.interest_rate) : null,
            monthly_payment: d.monthly_payment ? Number(d.monthly_payment) : null,
          })) : [],
        };
        if (groupId) payload.application_group_id = groupId;
        payload[dataKey] = formData[dataKey] || {};

        if (resumeAppId && type === loanTypes[0]) {
          await axios.put(`${BACKEND_URL}/api/applications/${resumeAppId}/resume`, payload);
        } else if (!resumeAppId) {
          await axios.post(`${BACKEND_URL}/api/applications`, payload);
        }
      }
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Save draft error:', error?.response?.data || error);
      const detail = error?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail
        : Array.isArray(detail) ? detail.map(e => e.msg || e.loc?.join('.')).join(', ')
        : 'Please try again.';
      alert(`Error saving draft: ${msg}`);
    } finally {
      setSavingDraft(false);
    }
  };

  // Submit application
  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const groupId = loanTypes.length > 1 ? crypto.randomUUID() : null;
      for (const type of loanTypes) {
        const dataKey = `${type}_data`;
        const payload = {
          product_type: type,
          has_business_debts: hasDebts,
          debt_schedule: hasDebts ? debtItems.filter(d => d.lender_name).map(d => ({
            ...d,
            original_amount: d.original_amount ? Number(d.original_amount) : null,
            current_balance: d.current_balance ? Number(d.current_balance) : null,
            interest_rate: d.interest_rate ? Number(d.interest_rate) : null,
            monthly_payment: d.monthly_payment ? Number(d.monthly_payment) : null,
          })) : [],
        };
        if (groupId) payload.application_group_id = groupId;
        payload[dataKey] = formData[dataKey] || {};

        if (resumeAppId) {
          await axios.put(`${BACKEND_URL}/api/applications/${resumeAppId}/resume`, payload);
        } else {
          await axios.post(`${BACKEND_URL}/api/applications`, payload);
        }
      }
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Submit error:', error?.response?.data || error);
      const detail = error?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail
        : Array.isArray(detail) ? detail.map(e => e.msg || e.loc?.join('.')).join(', ')
        : 'Some required fields may be missing.';
      alert(`Error submitting application: ${msg}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleFieldEdit = (dataKey, fieldKey, value) => {
    setFormData(prev => {
      const updated = { ...prev, [dataKey]: { ...(prev[dataKey] || {}), [fieldKey]: value } };
      if (loanTypes.length > 1 && value !== '' && value !== null && value !== undefined) {
        const mapping = REVERSE_LOOKUP[`${dataKey}.${fieldKey}`];
        if (mapping) {
          const activeDataKeys = loanTypes.map(t => `${t}_data`);
          for (const [targetDataKey, targetField] of Object.entries(mapping)) {
            if (targetDataKey === dataKey || !activeDataKeys.includes(targetDataKey)) continue;
            if (!updated[targetDataKey]) updated[targetDataKey] = {};
            updated[targetDataKey] = { ...updated[targetDataKey], [targetField]: value };
          }
        }
      }
      return updated;
    });
  };

  const getFilledCount = (dataKey) => {
    const data = formData[dataKey] || {};
    return Object.values(data).filter(v => v !== null && v !== undefined && v !== '' && v !== 0).length;
  };

  const getMissingRequired = (loanType) => {
    const dataKey = `${loanType}_data`;
    const data = formData[dataKey] || {};
    return (REQUIRED_FIELDS[loanType] || []).filter(key => {
      const val = data[key];
      return val === null || val === undefined || val === '';
    });
  };

  const getTotalMissing = () => loanTypes.reduce((sum, t) => sum + getMissingRequired(t).length, 0);

  // Debt schedule helpers
  const addDebtRow = () => setDebtItems(prev => [...prev, { ...EMPTY_DEBT }]);
  const removeDebtRow = (idx) => setDebtItems(prev => prev.filter((_, i) => i !== idx));
  const updateDebtField = (idx, field, value) => {
    setDebtItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  };

  const debtTotalBalance = debtItems.reduce((sum, d) => sum + (parseFloat(d.current_balance) || 0), 0);
  const debtTotalMonthly = debtItems.reduce((sum, d) => sum + (parseFloat(d.monthly_payment) || 0), 0);

  // ============ RENDER ============

  // Step 1: Loan Type Selection
  if (step === 'select') {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex flex-col sm:flex-row sm:items-center sm:justify-center overflow-y-auto" data-testid="ai-app-modal">
        <div className="bg-white sm:rounded-2xl w-full sm:max-w-lg shadow-2xl min-h-screen sm:min-h-0 sm:max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between p-6 border-b flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-[#081120] flex items-center justify-center">
                <Sparkles className="h-4 w-4 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-800">New Application</h2>
                <p className="text-sm text-slate-500">AI-guided application process</p>
              </div>
            </div>
            <button onClick={onClose} className="h-9 w-9 flex items-center justify-center rounded-lg border-2 border-slate-300 bg-slate-100 text-slate-600 hover:bg-red-50 hover:border-red-400 hover:text-red-600 transition-all duration-200" data-testid="close-ai-app"><X className="h-5 w-5" /></button>
          </div>
          <div className="p-6 overflow-y-auto flex-1 modal-scroll-area" style={{ WebkitOverflowScrolling: 'touch' }}>
            <p className="text-sm text-slate-600 mb-4">Select the loan type(s) you'd like to apply for:</p>
            <div className="space-y-3">
              {availableLoanTypes.map(opt => {
                const selected = loanTypes.includes(opt.id);
                return (
                  <button key={opt.id} onClick={() => toggleLoanType(opt.id)}
                    className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left ${selected ? `${opt.color} border-current` : 'border-slate-200 hover:border-slate-300 text-slate-800'}`}
                    data-testid={`select-${opt.id}`}>
                    {selected ? <CheckCircle className="h-5 w-5 flex-shrink-0" /> : <Circle className="h-5 w-5 text-slate-400 flex-shrink-0" />}
                    <div>
                      <div className="font-medium">{opt.label}</div>
                      <div className="text-sm text-slate-500">{opt.desc}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="p-6 border-t flex justify-end flex-shrink-0 bg-white">
            <Button onClick={startApplication} disabled={loanTypes.length === 0} className="bg-[#081120] hover:bg-[#0E333F]" data-testid="start-application-btn">
              <FileText className="h-4 w-4 mr-2" /> Continue
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Step: Debt Schedule
  if (step === 'debt_schedule') {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex flex-col sm:flex-row sm:items-center sm:justify-center overflow-y-auto" data-testid="debt-schedule-modal">
        <div className="bg-white sm:rounded-2xl w-full sm:max-w-5xl min-h-screen sm:min-h-0 sm:h-[90vh] shadow-2xl flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b bg-white flex-shrink-0">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => setStep('review')} data-testid="back-to-review-from-debt">
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div>
                <h2 className="text-lg font-semibold text-slate-800">Business Debt Schedule</h2>
                <p className="text-sm text-slate-500">List all outstanding business debts</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={handleSaveForLater} disabled={savingDraft} className="border-2 border-amber-500 text-amber-700 font-semibold hover:bg-amber-500 hover:text-white" data-testid="save-draft-debt-btn">
                {savingDraft ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <PauseCircle className="h-4 w-4 mr-2" />}
                Save for Later
              </Button>
              <Button onClick={() => setStep('review')} className="bg-[#081120] hover:bg-[#0E333F]" data-testid="continue-to-review-btn">
                Continue to Review <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
              </Button>
              <button onClick={onClose} className="h-9 w-9 flex items-center justify-center rounded-lg border-2 border-slate-300 bg-slate-100 text-slate-600 hover:bg-red-50 hover:border-red-400 hover:text-red-600 transition-all duration-200"><X className="h-5 w-5" /></button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6 bg-slate-50/50">
            <div className="max-w-4xl mx-auto">
              {/* Question: Does the business have debts? */}
              <Card className="mb-6">
                <CardContent className="pt-6">
                  <p className="text-sm font-medium text-slate-700 mb-4">Does this business currently have any outstanding debts?</p>
                  <div className="flex gap-3">
                    <Button variant={hasDebts === true ? 'default' : 'outline'} size="sm"
                      onClick={() => setHasDebts(true)} className={hasDebts === true ? 'bg-[#081120]' : ''} data-testid="has-debts-yes">
                      Yes
                    </Button>
                    <Button variant={hasDebts === false ? 'default' : 'outline'} size="sm"
                      onClick={() => setHasDebts(false)} className={hasDebts === false ? 'bg-[#081120]' : ''} data-testid="has-debts-no">
                      No
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Debt items table */}
              {hasDebts && (
                <div className="space-y-4">
                  {debtItems.map((debt, idx) => (
                    <Card key={idx} className="border-slate-200">
                      <CardContent className="pt-5 pb-4">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-sm font-semibold text-slate-700">Debt #{idx + 1}</h4>
                          {debtItems.length > 1 && (
                            <Button variant="ghost" size="sm" onClick={() => removeDebtRow(idx)}
                              className="h-7 w-7 p-0 text-red-400 hover:text-red-600" data-testid={`remove-debt-${idx}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-3">
                          {DEBT_FIELDS.map(field => (
                            <div key={field.key} className="space-y-1">
                              <label className="text-xs font-medium text-slate-500">{field.label}</label>
                              {field.type === 'select' ? (
                                <select value={debt[field.key] || ''} onChange={e => updateDebtField(idx, field.key, e.target.value)}
                                  className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-400"
                                  data-testid={`debt-${idx}-${field.key}`}>
                                  <option value="">Select...</option>
                                  {field.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                </select>
                              ) : (
                                <input type={field.type} value={debt[field.key] || ''}
                                  onChange={e => updateDebtField(idx, field.key, e.target.value)}
                                  placeholder={field.label}
                                  className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-400"
                                  data-testid={`debt-${idx}-${field.key}`} />
                              )}
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}

                  <Button variant="outline" onClick={addDebtRow} className="w-full" data-testid="add-debt-row-btn">
                    <Plus className="h-4 w-4 mr-2" /> Add Another Debt
                  </Button>

                  {/* Totals summary */}
                  <div className="flex gap-4 p-4 bg-slate-100 rounded-lg">
                    <div>
                      <p className="text-xs text-slate-500 font-medium">Total Current Balance</p>
                      <p className="text-lg font-bold text-slate-800" data-testid="debt-total-balance">${debtTotalBalance.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium">Total Monthly Payments</p>
                      <p className="text-lg font-bold text-slate-800" data-testid="debt-total-monthly">${debtTotalMonthly.toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              )}

              {hasDebts === false && (
                <div className="p-6 text-center text-slate-500 bg-white rounded-lg border border-slate-200">
                  <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-500" />
                  <p className="text-sm font-medium">No business debts reported</p>
                  <p className="text-xs text-slate-400 mt-1">You can proceed to review your application</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step: Review & Edit
  if (step === 'review') {
    const totalMissing = getTotalMissing();
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex flex-col sm:flex-row sm:items-center sm:justify-center overflow-y-auto" data-testid="ai-app-review-modal">
        <div className="bg-white sm:rounded-2xl w-full sm:max-w-5xl min-h-screen sm:min-h-0 sm:h-[90vh] shadow-2xl flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b bg-white flex-shrink-0">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => setStep('select')} data-testid="back-from-review-btn">
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div>
                <h2 className="text-lg font-semibold text-slate-800">Application</h2>
                <p className="text-sm text-slate-500">Fill in application details or use AI Assistant</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {totalMissing > 0 && (
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
                  <span className="text-xs font-medium text-amber-700">{totalMissing} required field{totalMissing !== 1 ? 's' : ''} missing</span>
                </div>
              )}
              <Button onClick={goToAIChat} size="sm" className="bg-[#081120] hover:bg-[#0E333F] text-white font-semibold" data-testid="ai-assistant-btn">
                <Sparkles className="h-4 w-4 mr-1.5" /> AI Assistant
              </Button>
              {needsDebtSchedule && (
                <Button onClick={() => setStep('debt_schedule')} size="sm" variant="outline" className="border-2 border-[#081120] text-[#081120] font-semibold hover:bg-[#081120] hover:text-white" data-testid="debt-schedule-from-review-btn">
                  <DollarSign className="h-4 w-4 mr-1" /> Debt Schedule
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={() => { handleSaveForLater(); }} disabled={savingDraft} data-testid="save-draft-review-btn" className="border-2 border-amber-500 text-amber-700 font-semibold hover:bg-amber-500 hover:text-white">
                {savingDraft ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <PauseCircle className="h-4 w-4 mr-2" />}
                Save & Exit
              </Button>
              <Button onClick={handleSubmit} disabled={submitting || totalMissing > 0} className="bg-green-600 hover:bg-green-700" data-testid="submit-application-btn">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                {submitting ? 'Submitting...' : 'Submit Application'}
              </Button>
              <button onClick={onClose} className="h-9 w-9 flex items-center justify-center rounded-lg border-2 border-slate-300 bg-slate-100 text-slate-600 hover:bg-red-50 hover:border-red-400 hover:text-red-600 transition-all duration-200"><X className="h-5 w-5" /></button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6 bg-slate-50/50">
            <div className="max-w-4xl mx-auto space-y-6">
              {loanTypes.map(type => {
                const dataKey = `${type}_data`;
                const sections = FIELD_SECTIONS[type] || [];
                const missing = getMissingRequired(type);
                const requiredSet = new Set(REQUIRED_FIELDS[type] || []);
                return (
                  <div key={type}>
                    <div className="flex items-center gap-3 mb-4">
                      <h3 className="text-base font-bold text-slate-800">
                        {LOAN_TYPE_OPTIONS.find(o => o.id === type)?.label || type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </h3>
                      {missing.length > 0 ? (
                        <Badge className="bg-amber-100 text-amber-800 text-xs">{missing.length} missing</Badge>
                      ) : (
                        <Badge className="bg-green-100 text-green-800 text-xs">Complete</Badge>
                      )}
                    </div>
                    {sections.map(section => {
                      // Repeater section — dynamic list of grouped rows.
                      // Currently used by Debt Consolidation to allow any
                      // number of outstanding debts/advances.
                      if (section.fmt === 'repeater') {
                        const rows = (formData[dataKey] || {})[section.repeaterKey] || [];
                        const setRows = (newRows) => setFormData(prev => ({
                          ...prev,
                          [dataKey]: { ...(prev[dataKey] || {}), [section.repeaterKey]: newRows }
                        }));
                        return (
                          <Card key={section.title} className="mb-4">
                            <CardContent className="pt-5 pb-4 px-5">
                              <div className="flex items-center justify-between mb-4">
                                <h4 className="text-sm font-semibold text-slate-600 uppercase tracking-wide">
                                  {section.title}
                                  <span className="ml-2 text-xs font-normal text-slate-400">({rows.length})</span>
                                </h4>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setRows([...rows, {}])}
                                  data-testid={`repeater-add-${section.repeaterKey}`}
                                >
                                  + {section.addButtonLabel || 'Add Row'}
                                </Button>
                              </div>
                              {rows.length === 0 ? (
                                <p className="text-sm text-slate-400 italic">No entries yet. Click &quot;{section.addButtonLabel || 'Add Row'}&quot; to add one.</p>
                              ) : rows.map((row, rowIdx) => (
                                <div key={rowIdx} className="mb-4 pb-4 border-b border-slate-100 last:mb-0 last:pb-0 last:border-b-0" data-testid={`repeater-row-${section.repeaterKey}-${rowIdx}`}>
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs font-medium text-slate-500 uppercase">
                                      {section.title.replace(/s\s*\/.*$/, '').replace(/s$/, '')} #{rowIdx + 1}
                                    </span>
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="ghost"
                                      className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7 px-2"
                                      onClick={() => setRows(rows.filter((_, i) => i !== rowIdx))}
                                      data-testid={`repeater-remove-${section.repeaterKey}-${rowIdx}`}
                                    >
                                      Remove
                                    </Button>
                                  </div>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                                    {section.fields.map(field => {
                                      const val = row[field.key] ?? '';
                                      const isCurrency = field.fmt === 'currency';
                                      return (
                                        <div key={field.key} className="space-y-1">
                                          <Label className="text-xs text-slate-600">{field.label}</Label>
                                          <Input
                                            type={isCurrency ? 'number' : 'text'}
                                            value={val}
                                            onChange={(e) => {
                                              const next = [...rows];
                                              next[rowIdx] = { ...next[rowIdx], [field.key]: isCurrency && e.target.value !== '' ? parseFloat(e.target.value) : e.target.value };
                                              setRows(next);
                                            }}
                                            className="text-sm h-9"
                                            data-testid={`repeater-field-${section.repeaterKey}-${rowIdx}-${field.key}`}
                                          />
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              ))}
                            </CardContent>
                          </Card>
                        );
                      }
                      return (
                      <Card key={section.title} className="mb-4">
                        <CardContent className="pt-5 pb-4 px-5">
                          <h4 className="text-sm font-semibold text-slate-600 mb-4 uppercase tracking-wide">{section.title}</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                            {section.fields.map(field => {
                              // Skip fields whose showIf predicate says they
                              // are not applicable given the current form
                              // state (e.g. Entity Name / EIN only appear if
                              // "title_held_in_entity" is "Yes").
                              if (typeof field.showIf === 'function' && !field.showIf(formData[dataKey] || {})) {
                                return null;
                              }
                              const rawValue = (formData[dataKey] || {})[field.key];
                              const isRequired = requiredSet.has(field.key);
                              const isEmpty = rawValue === null || rawValue === undefined || rawValue === '' || (Array.isArray(rawValue) && rawValue.length === 0);
                              const isMissing = isRequired && isEmpty;
                              const colSpanClass = field.fmt === 'textarea' || field.fmt === 'multiselect' ? 'md:col-span-2' : '';
                              return (
                                <div key={field.key} className={`space-y-1 ${colSpanClass}`}>
                                  <label className="text-xs font-medium text-slate-500 flex items-center gap-1">
                                    {field.label}
                                    {isRequired && <span className="text-red-400">*</span>}
                                  </label>
                                  {field.fmt === 'bool' ? (
                                    <select value={rawValue === true || rawValue === 'true' || rawValue === 'Yes' ? 'Yes' : rawValue === false || rawValue === 'false' || rawValue === 'No' ? 'No' : ''}
                                      onChange={e => handleFieldEdit(dataKey, field.key, e.target.value === '' ? '' : e.target.value)}
                                      className={`w-full h-9 px-3 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-slate-400 ${isMissing ? 'border-red-300 bg-red-50' : 'border-slate-200'}`}
                                      data-testid={`review-field-${field.key}`}>
                                      <option value="">Select...</option>
                                      <option value="Yes">Yes</option>
                                      <option value="No">No</option>
                                    </select>
                                  ) : field.fmt === 'select' ? (
                                    <select value={rawValue ?? ''}
                                      onChange={e => handleFieldEdit(dataKey, field.key, e.target.value)}
                                      className={`w-full h-9 px-3 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-slate-400 ${isMissing ? 'border-red-300 bg-red-50' : 'border-slate-200'}`}
                                      data-testid={`review-field-${field.key}`}>
                                      <option value="">Select...</option>
                                      {(field.options || []).map(opt => (
                                        <option key={opt} value={opt}>{opt}</option>
                                      ))}
                                    </select>
                                  ) : field.fmt === 'multiselect' ? (
                                    <div className={`flex flex-wrap gap-3 px-3 py-2 border rounded-md ${isMissing ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-white'}`} data-testid={`review-field-${field.key}`}>
                                      {(field.options || []).map((opt, oi) => {
                                        const arr = Array.isArray(rawValue) ? rawValue : [];
                                        const storedValue = (field.optionValues && field.optionValues[oi]) ?? opt;
                                        const checked = arr.includes(storedValue);
                                        return (
                                          <label key={opt} className="flex items-center gap-1.5 text-sm cursor-pointer">
                                            <input type="checkbox" checked={checked}
                                              onChange={e => {
                                                const next = e.target.checked ? [...arr, storedValue] : arr.filter(v => v !== storedValue);
                                                handleFieldEdit(dataKey, field.key, next);
                                              }} />
                                            {opt}
                                          </label>
                                        );
                                      })}
                                    </div>
                                  ) : field.fmt === 'textarea' ? (
                                    <textarea value={rawValue ?? ''}
                                      onChange={e => handleFieldEdit(dataKey, field.key, e.target.value)}
                                      rows={4}
                                      placeholder={isMissing ? 'Required' : `Enter ${field.label.toLowerCase()}`}
                                      className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-slate-400 ${isMissing ? 'border-red-300 bg-red-50 placeholder:text-red-400' : 'border-slate-200'}`}
                                      data-testid={`review-field-${field.key}`} />
                                  ) : (
                                    <input type={field.fmt === 'currency' ? 'number' : 'text'}
                                      value={SSN_KEYS.has(field.key) ? formatSSN(rawValue) : (rawValue ?? '')}
                                      onChange={e => {
                                        const val = SSN_KEYS.has(field.key) ? formatSSN(e.target.value) : (field.fmt === 'currency' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value);
                                        handleFieldEdit(dataKey, field.key, val);
                                      }}
                                      placeholder={isMissing ? 'Required' : (SSN_KEYS.has(field.key) ? 'XXX-XX-XXXX' : `Enter ${field.label.toLowerCase()}`)}
                                      className={`w-full h-9 px-3 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-slate-400 ${isMissing ? 'border-red-300 bg-red-50 placeholder:text-red-400' : 'border-slate-200'}`}
                                      data-testid={`review-field-${field.key}`} />
                                  )}
                                </div>
                              );
                            })}
                          </div>
                          {/* Computed fields for SBA Business Financials */}
                          {section.title === 'Business Financials' && type === 'sba_loan' && (() => {
                            const d = formData.sba_loan_data || {};
                            const revenue = parseFloat(d.revenue) || 0;
                            const cogs = parseFloat(d.cost_of_goods_sold) || 0;
                            const payroll = parseFloat(d.payroll) || 0;
                            const rent = parseFloat(d.rent) || 0;
                            const otherExp = parseFloat(d.other_operating_expenses) || 0;
                            const grossMargin = revenue - cogs;
                            const operatingCashFlow = grossMargin - payroll - rent - otherExp;
                            return (
                              <div className="mt-4 pt-4 border-t border-slate-200 space-y-3">
                                <div className="grid grid-cols-2 gap-4">
                                  <div className="p-3 bg-slate-100 rounded-lg">
                                    <p className="text-xs text-slate-500 font-medium">Gross Margin</p>
                                    <p className={`text-lg font-bold ${grossMargin >= 0 ? 'text-emerald-700' : 'text-red-600'}`} data-testid="computed-gross-margin">
                                      ${grossMargin.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                    </p>
                                  </div>
                                  <div className="p-3 bg-slate-100 rounded-lg">
                                    <p className="text-xs text-slate-500 font-medium">Operating Cash Flow</p>
                                    <p className={`text-lg font-bold ${operatingCashFlow >= 0 ? 'text-emerald-700' : 'text-red-600'}`} data-testid="computed-operating-cash-flow">
                                      ${operatingCashFlow.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                    </p>
                                  </div>
                                </div>
                                <label className="flex items-start gap-2 p-3 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50 mt-2">
                                  <input type="checkbox" checked={d.financial_attestation || false}
                                    onChange={e => handleFieldEdit('sba_loan_data', 'financial_attestation', e.target.checked)}
                                    className="mt-0.5 h-4 w-4 accent-slate-700" data-testid="financial-attestation-checkbox" />
                                  <span className="text-xs text-slate-600 leading-relaxed">
                                    The financial information provided in this loan application and the information provided in all supporting documents and forms are true and accurate in all material respects.
                                  </span>
                                </label>
                              </div>
                            );
                          })()}
                        </CardContent>
                      </Card>
                      );
                    })}

                    {/* Additional Owners */}
                    {(type === 'sba_loan' || type === 'term_loan_10yr' || type === 'term_loan_3_5yr' || type === 'term_loan' || type === 'term_loc_hybrid') && (() => {
                      const data = formData[dataKey] || {};
                      const owners = data.additional_owners || [];
                      const parsePct = (val) => {
                        if (val === null || val === undefined || val === '') return 0;
                        const num = parseFloat(String(val).replace(/[%,]/g, ''));
                        return isNaN(num) ? 0 : num;
                      };
                      const primaryPct = parsePct(data.ownership_percentage || data.percentage_of_ownership);
                      const ownersPct = owners.reduce((sum, o) => sum + parsePct(o.percentage_of_ownership || o.ownership_percentage), 0);
                      const totalPct = primaryPct + ownersPct;
                      const OWNER_FIELDS = [
                        { key: 'first_name', label: 'First Name' },
                        { key: 'last_name', label: 'Last Name' },
                        { key: 'percentage_of_ownership', label: 'Ownership %', type: 'number' },
                        { key: 'email_address', label: 'Email' },
                        { key: 'mobile_phone_number', label: 'Phone' },
                        { key: 'date_of_birth', label: 'Date of Birth' },
                        { key: 'social_security_number', label: 'SSN' },
                        { key: 'home_address', label: 'Home Address' },
                      ];
                      const addOwner = () => {
                        const updated = [...owners, { first_name: '', last_name: '', first_last_name: '', percentage_of_ownership: 0, email_address: '', mobile_phone_number: '', date_of_birth: '', social_security_number: '', home_address: '' }];
                        setFormData(prev => ({ ...prev, [dataKey]: { ...prev[dataKey], additional_owners: updated } }));
                      };
                      const updateOwner = (idx, field, value) => {
                        const updated = owners.map((o, i) => i === idx ? { ...o, [field]: value } : o);
                        setFormData(prev => ({ ...prev, [dataKey]: { ...prev[dataKey], additional_owners: updated } }));
                      };
                      const removeOwner = (idx) => {
                        const updated = owners.filter((_, i) => i !== idx);
                        setFormData(prev => ({ ...prev, [dataKey]: { ...prev[dataKey], additional_owners: updated } }));
                      };
                      return (
                        <Card className="mb-4 border-slate-300">
                          <CardContent className="pt-5 pb-4 px-5">
                            <div className="flex items-center justify-between mb-4">
                              <h4 className="text-sm font-semibold text-slate-600 uppercase tracking-wide">Additional Owners</h4>
                              <span className={`text-xs font-medium px-2 py-1 rounded ${totalPct >= 80 ? 'bg-green-100 text-green-700' : primaryPct > 0 && primaryPct < 100 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>
                                Total: {totalPct.toFixed(0)}%
                              </span>
                            </div>
                            {primaryPct > 0 && primaryPct < 100 && totalPct < 80 && (
                              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
                                <AlertTriangle className="h-3.5 w-3.5 inline mr-1" />
                                Total ownership must reach at least 80%. Currently at {totalPct.toFixed(0)}%.
                              </div>
                            )}
                            {owners.map((owner, idx) => (
                              <div key={idx} className="mb-4 p-4 bg-slate-50 border border-slate-200 rounded-lg relative">
                                <div className="flex items-center justify-between mb-3">
                                  <span className="text-xs font-semibold text-slate-700">Owner #{idx + 2}</span>
                                  <Button type="button" variant="ghost" size="sm" onClick={() => removeOwner(idx)}
                                    className="h-6 w-6 p-0 text-red-400 hover:text-red-600" data-testid={`remove-owner-${idx}`}>
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                                  {OWNER_FIELDS.map(f => (
                                    <div key={f.key} className="space-y-1">
                                      <label className="text-xs font-medium text-slate-500">{f.label}</label>
                                      <input type={f.type || 'text'} value={owner[f.key] ?? ''}
                                        onChange={e => updateOwner(idx, f.key, f.type === 'number' ? (e.target.value === '' ? 0 : Number(e.target.value)) : e.target.value)}
                                        placeholder={f.label}
                                        className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-400"
                                        data-testid={`owner-${idx}-${f.key}`} />
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                            <Button type="button" variant="outline" size="sm" onClick={addOwner} disabled={totalPct >= 100}
                              className="w-full" data-testid="add-additional-owner-btn">
                              <UserPlus className="h-4 w-4 mr-2" /> Add Additional Owner
                            </Button>
                          </CardContent>
                        </Card>
                      );
                    })()}
                  </div>
                );
              })}

              {/* Debt Schedule Summary in Review */}
              {needsDebtSchedule && hasDebts && debtItems.some(d => d.lender_name) && (
                <Card className="mb-4">
                  <CardContent className="pt-5 pb-4 px-5">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-slate-600 uppercase tracking-wide">Debt Schedule Summary</h4>
                      <Button variant="outline" size="sm" onClick={() => setStep('debt_schedule')} className="border-2 border-[#081120] text-[#081120] font-semibold hover:bg-[#081120] hover:text-white" data-testid="edit-debt-schedule-btn">
                        <Edit3 className="h-3.5 w-3.5 mr-1" /> Edit
                      </Button>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-left text-slate-500">
                            <th className="pb-2 font-medium">Creditor</th>
                            <th className="pb-2 font-medium">Type</th>
                            <th className="pb-2 font-medium text-right">Balance</th>
                            <th className="pb-2 font-medium text-right">Monthly Pmt</th>
                            <th className="pb-2 font-medium">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {debtItems.filter(d => d.lender_name).map((d, i) => (
                            <tr key={i}>
                              <td className="py-2 font-medium text-slate-800">{d.lender_name}</td>
                              <td className="py-2 text-slate-600">{d.type_of_debt}</td>
                              <td className="py-2 text-right text-slate-800">${(parseFloat(d.current_balance) || 0).toLocaleString()}</td>
                              <td className="py-2 text-right text-slate-800">${(parseFloat(d.monthly_payment) || 0).toLocaleString()}</td>
                              <td className="py-2 text-slate-600">{d.current_or_delinquent}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="font-semibold text-slate-800 border-t border-slate-300">
                            <td className="pt-2" colSpan={2}>Total</td>
                            <td className="pt-2 text-right">${debtTotalBalance.toLocaleString()}</td>
                            <td className="pt-2 text-right">${debtTotalMonthly.toLocaleString()}</td>
                            <td></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step 2: Chat + Form Preview
  const handleGoToReview = () => {
    if (needsDebtSchedule) {
      setStep('debt_schedule');
    } else {
      setStep('review');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-3" data-testid="ai-app-chat-modal">
      <div className="bg-white rounded-2xl w-full max-w-6xl h-[90vh] shadow-2xl flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b bg-white flex-shrink-0">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => setStep('review')} data-testid="back-to-review-from-chat">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="w-8 h-8 rounded-lg bg-[#081120] flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-slate-800">AI Application Assistant</h2>
              <div className="flex items-center gap-2">
                {loanTypes.map(t => (
                  <Badge key={t} variant="outline" className="text-[10px] py-0">
                    {LOAN_TYPE_OPTIONS.find(o => o.id === t)?.label || t.replace(/_/g, ' ').toUpperCase()}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <div className="w-32 h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-teal-600 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
              </div>
              <span className="text-xs text-slate-500 font-medium">{progress}%</span>
            </div>
            <Button variant="outline" size="sm" onClick={handleSaveForLater} disabled={savingDraft} className="border-2 border-amber-500 text-amber-700 font-semibold hover:bg-amber-500 hover:text-white" data-testid="save-draft-chat-btn">
              {savingDraft ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <PauseCircle className="h-4 w-4 mr-1" />}
              <span className="hidden sm:inline">Save for Later</span>
            </Button>
            {needsDebtSchedule && (
              <Button onClick={() => setStep('debt_schedule')} size="sm" className="bg-[#081120] hover:bg-[#162538] text-white font-semibold" data-testid="debt-schedule-btn">
                <DollarSign className="h-4 w-4 mr-1" /> Debt Schedule
              </Button>
            )}
            <Button onClick={() => setStep('review')} size="sm" className={progress >= 80 ? "bg-green-600 hover:bg-green-700 text-white font-semibold" : "bg-[#081120] hover:bg-[#162538] text-white font-semibold"} data-testid="review-app-btn">
              <Edit3 className="h-4 w-4 mr-1.5" /> Review & Submit
            </Button>
            <Button variant="ghost" size="sm" onClick={onClose}><X className="h-4 w-4" /></Button>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Left: Chat */}
          <div className="flex-1 flex flex-col border-r min-w-0">
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 bg-slate-50/40">
              {messages.map((msg, i) => {
                const isUser = msg.role === 'user';
                return (
                  <div key={i} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                    <div className={`flex items-start gap-2.5 max-w-[85%] ${isUser ? 'flex-row-reverse' : ''}`}>
                      {!isUser && (
                        <div className="w-7 h-7 rounded-lg bg-[#081120] flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Sparkles className="h-3.5 w-3.5 text-white" />
                        </div>
                      )}
                      <div className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${isUser ? 'bg-[#081120] text-white rounded-br-md' : 'bg-white text-slate-700 border border-slate-200 rounded-bl-md shadow-sm'}`}>
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
              {(loading || uploadingDoc) && (
                <div className="flex justify-start">
                  <div className="flex items-start gap-2.5">
                    <div className="w-7 h-7 rounded-lg bg-[#081120] flex items-center justify-center flex-shrink-0">
                      <Sparkles className="h-3.5 w-3.5 text-white" />
                    </div>
                    <div className="px-4 py-3 rounded-2xl rounded-bl-md bg-white border border-slate-200 shadow-sm">
                      <div className="flex items-center gap-2">
                        <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />
                        <span className="text-sm text-slate-500">{uploadingDoc ? 'Reading document...' : 'Thinking...'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="px-4 py-3 border-t bg-white flex-shrink-0">
              {voiceError && <p className="text-xs text-red-500 mb-1.5 px-1">{voiceError}</p>}
              <form onSubmit={handleSend} className="flex items-end gap-2">
                <input type="file" ref={fileInputRef} onChange={handleDocUpload} className="hidden" accept=".pdf,.png,.jpg,.jpeg,.txt,.csv,.xls,.xlsx" />
                <Button type="button" variant="ghost" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploadingDoc}
                  className="text-slate-400 hover:text-slate-600 h-9 w-9 p-0 flex-shrink-0 mb-0.5" title="Upload document" data-testid="ai-app-upload-btn">
                  <Upload className="h-4 w-4" />
                </Button>
                <VoiceControls
                  onTranscription={(text) => setInput(prev => prev ? prev + ' ' + text : text)}
                  onError={(msg) => { setVoiceError(msg); setTimeout(() => setVoiceError(''), 4000); }}
                  textToSpeak={lastAIResponse}
                />
                <textarea value={input} onChange={e => { setInput(e.target.value); e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px'; }}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(e); } }}
                  placeholder="Type your response or use voice... (Shift+Enter for new line)" disabled={loading || uploadingDoc}
                  rows={2}
                  className="flex-1 text-sm text-slate-800 placeholder:text-slate-400 border border-slate-200 rounded-md px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-slate-300 disabled:opacity-50" data-testid="ai-app-input" />
                <Button type="submit" disabled={!input.trim() || loading} className="bg-[#081120] hover:bg-[#0E333F] h-9 w-9 p-0 flex-shrink-0 mb-0.5" data-testid="ai-app-send-btn">
                  <Send className="h-4 w-4" />
                </Button>
              </form>
            </div>
          </div>

          {/* Right: Form Preview */}
          <div className="w-80 lg:w-96 overflow-y-auto bg-white flex-shrink-0">
            <div className="p-4 border-b bg-slate-50">
              <h3 className="text-sm font-semibold text-slate-700">Application Preview</h3>
              <p className="text-[11px] text-slate-500 mt-0.5">Fields populate as you answer</p>
            </div>
            <div className="p-4 space-y-5">
              {loanTypes.map(type => {
                const dataKey = `${type}_data`;
                const sections = FIELD_SECTIONS[type] || [];
                const filled = getFilledCount(dataKey);
                const total = sections.reduce((sum, s) => sum + s.fields.length, 0);
                return (
                  <div key={type}>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                        {LOAN_TYPE_OPTIONS.find(o => o.id === type)?.label || type.replace(/_/g, ' ')}
                      </h4>
                      <Badge variant="outline" className="text-[10px]">{filled}/{total}</Badge>
                    </div>
                    {sections.map(section => (
                      <div key={section.title} className="mb-4">
                        <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">{section.title}</p>
                        <div className="space-y-1.5">
                          {section.fields.map(field => {
                            const raw = (formData[dataKey] || {})[field.key];
                            const value = formatValue(raw, field.fmt);
                            const isFilled = value !== null;
                            return (
                              <div key={field.key} className="flex items-center justify-between py-1 px-2 rounded text-xs group hover:bg-slate-50">
                                <span className={isFilled ? 'text-slate-600' : 'text-slate-400'}>{field.label}</span>
                                {isFilled ? (
                                  SSN_KEYS.has(field.key) ? <SSNValue value={raw} /> : <span className="font-medium text-slate-800 text-right max-w-[55%] truncate" title={value}>{value}</span>
                                ) : (
                                  <span className="text-slate-300">---</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIApplicationFlow;
